"use client";

import { useCallback, useEffect, useState } from "react";
import { useTranslations } from "next-intl";

interface BookingModalProps {
  mentorName: string;
  mentorId: string;
  onClose: () => void;
}

type FormState = "idle" | "submitting" | "success" | "error";

export function BookingModal({
  mentorName,
  mentorId,
  onClose,
}: BookingModalProps) {
  const t = useTranslations("booking");
  const [state, setState] = useState<FormState>("idle");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  // Close on Escape key
  const handleKey = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [handleKey]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setState("submitting");
    setErrorMsg("");

    try {
      const res = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name, email, message, mentorId }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error ?? "Something went wrong.");
      }

      setState("success");
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : t("errorFallback"));
      setState("error");
    }
  }

  return (
    /* Backdrop */
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      {/* Panel */}
      <div
        className="w-full sm:max-w-md rounded-2xl p-6 shadow-2xl"
        style={{
          background: "var(--color-bg)",
          color: "var(--color-text)",
        }}
      >
        {/* Header */}
        <div className="flex items-start justify-between mb-5">
          <div>
            <h2 className="text-base font-semibold">{t("title")}</h2>
            <p className="mt-0.5 text-sm" style={{ color: "var(--color-muted)" }}>
              {t("withMentor", { mentorName })}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md"
            style={{ color: "var(--color-subtle)" }}
            aria-label="Close"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {state === "success" ? (
          <div className="py-8 text-center">
            <div
              className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4"
              style={{
                background: "var(--color-surface)",
                color: "var(--color-brand)",
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M20 6 9 17l-5-5" />
              </svg>
            </div>
            <p className="font-medium" style={{ color: "var(--color-text)" }}>
              {t("successTitle")}
            </p>
            <p
              className="mt-1 text-sm"
              style={{ color: "var(--color-muted)" }}
            >
              {t("successText", { mentorName })}
            </p>
            <button
              onClick={onClose}
              className="mt-6 text-sm font-medium"
              style={{ color: "var(--color-brand)" }}
            >
              {t("close")}
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1">
              <label
                htmlFor="book-name"
                className="text-xs font-medium"
                style={{ color: "var(--color-muted)" }}
              >
                {t("yourName")}
              </label>
              <input
                id="book-name"
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 transition"
                style={{
                  background: "var(--color-surface)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text)",
                }}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="book-email"
                className="text-xs font-medium"
                style={{ color: "var(--color-muted)" }}
              >
                {t("email")}
              </label>
              <input
                id="book-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 transition"
                style={{
                  background: "var(--color-surface)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text)",
                }}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label
                htmlFor="book-message"
                className="text-xs font-medium"
                style={{ color: "var(--color-muted)" }}
              >
                {t("whatWorkOn")}
              </label>
              <textarea
                id="book-message"
                required
                rows={4}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                className="rounded-lg border px-3 py-2 text-sm outline-none focus:ring-2 transition resize-none"
                style={{
                  background: "var(--color-surface)",
                  borderColor: "var(--color-border)",
                  color: "var(--color-text)",
                }}
              />
            </div>

            {state === "error" && (
              <p className="text-xs" style={{ color: "var(--color-brand)" }}>
                {errorMsg}
              </p>
            )}

            <button
              type="submit"
              disabled={state === "submitting"}
              className="mt-1 rounded-lg px-4 py-2.5 text-sm font-semibold text-white transition-opacity disabled:opacity-60"
              style={{ background: "var(--color-brand)" }}
            >
              {state === "submitting" ? t("sending") : t("sendRequest")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
